import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Archive,
  ArrowLeft,
  BarChart3,
  BadgePercent,
  Bot,
  BookOpen,
  CheckCircle2,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Globe2,
  LayoutDashboard,
  LifeBuoy,
  LoaderCircle,
  LogOut,
  Mail,
  MapPin,
  MessageCircle,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  ReceiptText,
  RotateCcw,
  Search,
  Send,
  ShoppingCart,
  Smartphone,
  Scale,
  Star,
  Trash2,
  UploadCloud,
  Users,
  X,
} from "lucide-react";
import { cloneElement, useEffect, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/features/auth/AuthContext";
import { AdminEpisodeCommunityPanel } from "@/features/podcast/community/AdminEpisodeCommunityPanel";
import { AdminSalesPanel } from "@/features/sales/admin/AdminSalesPanel";
import { AdminTicketsPanel } from "@/features/support/AdminTicketsPanel";
import { CATEGORIES, getProductCategories } from "@/features/sales/storefront/catalog/data";
import type { Level, Product } from "@/features/sales/storefront/catalog/types";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { useCurrency } from "@/features/sales/storefront/currency/CurrencyContext";
import { appCheckFetch, firebaseApp } from "@/lib/firebase";

const ADMIN_SECTIONS = ["products", "sales", "tickets", "community", "subscribers", "coupons", "analytics"] as const;
const adminSearchSchema = z.object({
  tab: z.enum(ADMIN_SECTIONS).optional().catch(undefined),
});

export const Route = createFileRoute("/admin/dashboard")({
  validateSearch: adminSearchSchema,
  head: () => ({
    meta: [
      { title: "Administración | Socrattica" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminDashboard,
});

type AdminSection = (typeof ADMIN_SECTIONS)[number];
type ProductStatus = "draft" | "published" | "archived";
type MailchimpStatus = "subscribed" | "unsubscribed" | "cleaned" | "pending" | "transactional";

interface MailchimpContact {
  id: string;
  emailAddress: string;
  name?: string;
  status: MailchimpStatus;
  lastChanged?: string;
  tags: string[];
  archived?: boolean;
  archivedAt?: string;
}

interface AdminCoupon {
  id: string;
  email: string;
  scope: "specific" | "general";
  code: string;
  percent: number;
  status: "active" | "used" | "expired";
  createdAt?: string;
  expiresAt?: string;
  usedAt?: string;
}

interface ProductFormProps {
  editingProduct: Product | null;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const LEVELS: Level[] = ["A1", "A2", "B1", "B2", "C1", "C2", "all"];
const CURRENCIES = ["COP", "USD", "EUR", "MXN"] as const;
const FORMATS = ["PDF", "EPUB", "PDF + Audio"] as const;

function timestampToIso(value: unknown) {
  if (value && typeof value === "object" && "toDate" in value) {
    const toDate = (value as { toDate?: unknown }).toDate;
    if (typeof toDate === "function") {
      return (toDate.call(value) as Date).toISOString();
    }
  }
  return typeof value === "string" ? value : undefined;
}

function normalizeProduct(id: string, data: Record<string, unknown>): Product {
  const currency = CURRENCIES.includes(data.currency as (typeof CURRENCIES)[number])
    ? (data.currency as Product["currency"])
    : "COP";

  return {
    ...(data as unknown as Product),
    id,
    currency,
    createdAt: timestampToIso(data.createdAt),
    updatedAt: timestampToIso(data.updatedAt),
    publishedAt: timestampToIso(data.publishedAt) ?? String(data.publishedAt ?? ""),
  };
}

function slugify(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function buildSearchTerms(...values: string[]) {
  const words = values
    .join(" ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length >= 2);
  const terms = new Set<string>();
  for (const word of words) {
    for (let length = 2; length <= Math.min(word.length, 30); length += 1) {
      terms.add(word.slice(0, length));
      if (terms.size >= 200) return Array.from(terms);
    }
  }
  return Array.from(terms);
}

function safeFileName(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "-");
}

async function createCoverThumbnail(file: File): Promise<Blob> {
  const image = await createImageBitmap(file);
  try {
    const maxWidth = 720;
    const maxHeight = 1_020;
    const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("No pudimos preparar la miniatura de la portada.");
    context.drawImage(image, 0, 0, width, height);
    const thumbnail = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/webp", 0.82);
    });
    if (!thumbnail) throw new Error("No pudimos comprimir la portada.");
    return thumbnail;
  } finally {
    image.close();
  }
}

function formatBytes(value?: number) {
  if (!value) return "Sin archivo";
  if (value < 1_024 * 1_024) return `${Math.round(value / 1_024)} KB`;
  return `${(value / (1_024 * 1_024)).toFixed(1)} MB`;
}

function formatDate(value?: string) {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function CouponDateTime({ value }: { value?: string }) {
  if (!value) return <span>Sin fecha</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <span>Sin fecha</span>;
  return (
    <span className="block whitespace-nowrap leading-5">
      <span className="block">
        {new Intl.DateTimeFormat("es-CO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(date)}
      </span>
      <span className="block text-[11px] text-black/40">
        {new Intl.DateTimeFormat("es-CO", {
          hour: "numeric",
          minute: "2-digit",
        }).format(date)}
      </span>
    </span>
  );
}

function statusLabel(status: MailchimpStatus) {
  const labels: Record<MailchimpStatus, string> = {
    subscribed: "Suscrito",
    unsubscribed: "Desuscrito",
    cleaned: "Rebotado",
    pending: "Pendiente",
    transactional: "Transaccional",
  };
  return labels[status];
}

async function readApiResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") ?? "";
  const body = await response.text();

  if (!contentType.includes("application/json")) {
    throw new Error(
      response.ok
        ? "El servicio administrativo aún no está disponible."
        : "La función administrativa aún no está publicada. Actualiza el despliegue e inténtalo de nuevo.",
    );
  }

  try {
    return JSON.parse(body) as T;
  } catch {
    throw new Error("El servicio administrativo devolvió una respuesta inválida.");
  }
}

function AdminDashboard() {
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const { user, isAdmin, isLoading, logout } = useAuth();
  const [section, setSection] = useState<AdminSection>(tab ?? "products");
  const [supportQuery, setSupportQuery] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [contacts, setContacts] = useState<MailchimpContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsLoadingMore, setContactsLoadingMore] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [contactsNextOffset, setContactsNextOffset] = useState<number>();
  const [contactsNextArchivedCursor, setContactsNextArchivedCursor] = useState<string>();
  const [contactsHasMore, setContactsHasMore] = useState(false);
  const [contactsTotalActive, setContactsTotalActive] = useState(0);
  const [contactsTotalArchived, setContactsTotalArchived] = useState(0);
  const [contactSearch, setContactSearch] = useState("");
  const [contactAction, setContactAction] = useState<string | null>(null);
  const [coupons, setCoupons] = useState<AdminCoupon[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponsLoaded, setCouponsLoaded] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponSaving, setCouponSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  function selectSection(nextSection: AdminSection) {
    setSection(nextSection);
    void navigate({
      to: "/admin/dashboard",
      search: { tab: nextSection },
      replace: true,
    });
  }

  useEffect(() => {
    setSection(tab ?? "products");
  }, [tab]);

  useEffect(() => {
    if (!isLoading && !user) {
      void navigate({ to: "/acceso", replace: true });
    } else if (!isLoading && user && !isAdmin) {
      void navigate({ to: "/", replace: true });
    }
  }, [isAdmin, isLoading, navigate, user]);

  useEffect(() => {
    if (!isAdmin) return;
    let unsubscribe = () => {};
    let active = true;

    void import("firebase/firestore").then(({ collection, getFirestore, onSnapshot }) => {
      if (!active) return;
      unsubscribe = onSnapshot(
        collection(getFirestore(firebaseApp), "products"),
        (snapshot) => {
          if (!active) return;
          const normalizedProducts = snapshot.docs
            .map((entry) => normalizeProduct(entry.id, entry.data() as Record<string, unknown>))
            .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
          setProducts(normalizedProducts);
          setProductsLoading(false);
          const missingSearchTerms = normalizedProducts
            .filter((product) => !product.searchTerms?.length)
            .slice(0, 400);
          if (missingSearchTerms.length > 0) {
            void import("firebase/firestore").then(({ doc, getFirestore, updateDoc }) =>
              Promise.allSettled(
                missingSearchTerms.map((product) =>
                  updateDoc(doc(getFirestore(firebaseApp), "products", product.id), {
                    searchTerms: buildSearchTerms(
                      product.title,
                      product.subtitle ?? "",
                      product.description,
                      product.category,
                      product.author ?? "",
                    ),
                  }),
                ),
              ),
            );
          }
        },
        () => {
          if (!active) return;
          toast.error("No pudimos cargar los productos.");
          setProductsLoading(false);
        },
      );
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [isAdmin]);

  async function getAdminHeaders() {
    if (!user) throw new Error("La sesión de administrador no está disponible.");
    return {
      Authorization: `Bearer ${await user.getIdToken()}`,
      "Content-Type": "application/json",
    };
  }

  async function loadContacts(loadMore = false) {
    if (loadMore && !contactsHasMore) return;
    if (loadMore) setContactsLoadingMore(true);
    else setContactsLoading(true);
    try {
      const params = new URLSearchParams({ pageSize: "50" });
      if (loadMore && contactsNextOffset !== undefined) {
        params.set("offset", String(contactsNextOffset));
      }
      if (loadMore && contactsNextArchivedCursor) {
        params.set("archivedCursor", contactsNextArchivedCursor);
      }
      const response = await appCheckFetch(`/api/admin/mailchimp?${params}`, {
        headers: await getAdminHeaders(),
      });
      const data = await readApiResponse<{
        contacts?: MailchimpContact[];
        totalActive?: number;
        totalArchived?: number;
        activeHasMore?: boolean;
        archivedHasMore?: boolean;
        nextOffset?: number;
        nextArchivedCursor?: string;
        message?: string;
      }>(response);
      if (!response.ok) throw new Error(data.message || "No pudimos cargar los suscriptores.");
      setContacts((current) => {
        const merged = new Map(
          (loadMore ? current : []).map((contact) => [
            `${contact.archived ? "archived" : "active"}:${contact.id}`,
            contact,
          ]),
        );
        for (const contact of data.contacts ?? []) {
          merged.set(`${contact.archived ? "archived" : "active"}:${contact.id}`, contact);
        }
        return Array.from(merged.values());
      });
      setContactsNextOffset(data.nextOffset);
      setContactsNextArchivedCursor(data.nextArchivedCursor);
      setContactsHasMore(data.activeHasMore === true || data.archivedHasMore === true);
      setContactsTotalActive(data.totalActive ?? 0);
      setContactsTotalArchived(data.totalArchived ?? 0);
      setContactsLoaded(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos cargar los suscriptores.");
    } finally {
      if (loadMore) setContactsLoadingMore(false);
      else setContactsLoading(false);
    }
  }

  async function loadCoupons() {
    setCouponsLoading(true);
    try {
      const response = await appCheckFetch("/api/admin/coupons", {
        headers: await getAdminHeaders(),
      });
      const data = await readApiResponse<{ coupons?: AdminCoupon[]; message?: string }>(response);
      if (!response.ok) throw new Error(data.message || "No pudimos cargar los cupones.");
      setCoupons(data.coupons ?? []);
      setCouponsLoaded(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos cargar los cupones.");
    } finally {
      setCouponsLoading(false);
    }
  }

  async function createCoupon(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setCouponSaving(true);
    try {
      const response = await appCheckFetch("/api/admin/coupons", {
        method: "POST",
        headers: await getAdminHeaders(),
        body: JSON.stringify({
          percent: Number(data.get("percent")),
          scope: data.get("scope"),
          email: String(data.get("email") ?? "").trim(),
          expiresAt: data.get("expiresAt"),
        }),
      });
      const result = await readApiResponse<{ code?: string; message?: string }>(response);
      if (!response.ok) throw new Error(result.message || "No pudimos crear el cupón.");
      toast.success(`Cupón ${result.code} creado.`);
      form.reset();
      setShowCouponForm(false);
      await loadCoupons();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos crear el cupón.");
    } finally {
      setCouponSaving(false);
    }
  }

  useEffect(() => {
    if (section === "subscribers" && isAdmin && !contactsLoaded) {
      void loadContacts();
    }
    // loadContacts usa el usuario actual y se ejecuta solo al abrir esta sección.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsLoaded, isAdmin, section]);

  useEffect(() => {
    if (section === "coupons" && isAdmin && !couponsLoaded) void loadCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponsLoaded, isAdmin, section]);

  async function handleProductSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || !isAdmin) return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    const title = String(formData.get("title") ?? "").trim();
    const customSlug = String(formData.get("slug") ?? "").trim();
    const coverFile = formData.get("cover");
    const digitalFile = formData.get("digitalFile");
    const sampleFile = formData.get("sampleFile");
    const selectedCover = coverFile instanceof File && coverFile.size > 0 ? coverFile : null;
    const selectedDigital =
      digitalFile instanceof File && digitalFile.size > 0 ? digitalFile : null;
    const selectedSample = sampleFile instanceof File && sampleFile.size > 0 ? sampleFile : null;
    const removeSample = formData.get("removeSample") === "on";

    if (!title) {
      toast.error("Escribe el nombre del producto.");
      return;
    }
    if (!editingProduct && !selectedDigital) {
      toast.error("Selecciona el archivo digital del producto.");
      return;
    }
    if (selectedCover && selectedCover.size > 10 * 1_024 * 1_024) {
      toast.error("La portada no puede superar 10 MB.");
      return;
    }
    if (selectedDigital && selectedDigital.size > 200 * 1_024 * 1_024) {
      toast.error("El archivo digital no puede superar 200 MB.");
      return;
    }
    if (selectedSample && selectedSample.size > 30 * 1_024 * 1_024) {
      toast.error("La muestra no puede superar 30 MB.");
      return;
    }
    if (
      selectedSample &&
      selectedSample.type !== "application/pdf" &&
      !selectedSample.name.toLowerCase().endsWith(".pdf")
    ) {
      toast.error("La muestra debe ser un archivo PDF.");
      return;
    }

    setSavingProduct(true);
    let newCoverPath: string | undefined;
    let newCoverThumbnailPath: string | undefined;
    let newFilePath: string | undefined;
    let newSamplePath: string | undefined;

    try {
      const firestore = await import("firebase/firestore");
      const storage = await import("firebase/storage");
      const database = firestore.getFirestore(firebaseApp);
      const productRef = editingProduct
        ? firestore.doc(database, "products", editingProduct.id)
        : firestore.doc(firestore.collection(database, "products"));
      const productId = productRef.id;

      let coverImage = editingProduct?.coverImage;
      let coverPath = editingProduct?.coverPath;
      let coverThumbnail = editingProduct?.coverThumbnail;
      let coverThumbnailPath = editingProduct?.coverThumbnailPath;
      let filePath = editingProduct?.filePath;
      let fileVersionId = editingProduct?.fileVersionId;
      let fileName = editingProduct?.fileName;
      let fileSize = editingProduct?.fileSize;
      let sampleUrl = editingProduct?.sampleUrl;
      let samplePath = editingProduct?.samplePath;
      let sampleFileName = editingProduct?.sampleFileName;
      let sampleFileSize = editingProduct?.sampleFileSize;
      const assetVersions = [...(editingProduct?.assetVersions ?? [])];

      if (selectedCover) {
        const uploadId = Date.now();
        newCoverPath = `product-covers/${productId}/${uploadId}-${safeFileName(selectedCover.name)}`;
        newCoverThumbnailPath = `product-covers/${productId}/${uploadId}-thumbnail.webp`;
        const coverReference = storage.ref(storage.getStorage(firebaseApp), newCoverPath);
        await storage.uploadBytes(coverReference, selectedCover, {
          contentType: selectedCover.type || "image/jpeg",
          cacheControl: "public,max-age=31536000,immutable",
        });
        const thumbnailReference = storage.ref(
          storage.getStorage(firebaseApp),
          newCoverThumbnailPath,
        );
        const thumbnail = await createCoverThumbnail(selectedCover);
        await storage.uploadBytes(thumbnailReference, thumbnail, {
          contentType: "image/webp",
          cacheControl: "public,max-age=31536000,immutable",
        });
        coverImage = await storage.getDownloadURL(coverReference);
        coverPath = newCoverPath;
        coverThumbnail = await storage.getDownloadURL(thumbnailReference);
        coverThumbnailPath = newCoverThumbnailPath;
      }

      if (selectedDigital) {
        const versionId = `${Date.now()}-${crypto.randomUUID()}`;
        newFilePath = `product-files/${productId}/${versionId}-${safeFileName(selectedDigital.name)}`;
        const fileReference = storage.ref(storage.getStorage(firebaseApp), newFilePath);
        await storage.uploadBytes(fileReference, selectedDigital, {
          contentType: selectedDigital.type || "application/octet-stream",
        });
        if (
          editingProduct?.filePath &&
          editingProduct.fileName &&
          editingProduct.fileSize &&
          !assetVersions.some((version) => version.path === editingProduct.filePath)
        ) {
          assetVersions.push({
            id: editingProduct.fileVersionId ?? `legacy-${productId}`,
            path: editingProduct.filePath,
            fileName: editingProduct.fileName,
            fileSize: editingProduct.fileSize,
            createdAt:
              editingProduct.updatedAt ?? editingProduct.createdAt ?? new Date().toISOString(),
          });
        }
        filePath = newFilePath;
        fileVersionId = versionId;
        fileName = selectedDigital.name;
        fileSize = selectedDigital.size;
        assetVersions.push({
          id: versionId,
          path: newFilePath,
          fileName: selectedDigital.name,
          fileSize: selectedDigital.size,
          createdAt: new Date().toISOString(),
        });
      }

      const manualSampleUrl = String(formData.get("sampleUrl") ?? "").trim();
      if (selectedSample) {
        const uploadId = `${Date.now()}-${crypto.randomUUID()}`;
        newSamplePath = `product-samples/${productId}/${uploadId}-${safeFileName(
          selectedSample.name,
        )}`;
        const sampleReference = storage.ref(storage.getStorage(firebaseApp), newSamplePath);
        await storage.uploadBytes(sampleReference, selectedSample, {
          contentType: "application/pdf",
          cacheControl: "public,max-age=3600",
        });
        sampleUrl = await storage.getDownloadURL(sampleReference);
        samplePath = newSamplePath;
        sampleFileName = selectedSample.name;
        sampleFileSize = selectedSample.size;
      } else if (removeSample) {
        sampleUrl = undefined;
        samplePath = undefined;
        sampleFileName = undefined;
        sampleFileSize = undefined;
      } else if (manualSampleUrl) {
        sampleUrl = manualSampleUrl;
        if (manualSampleUrl !== editingProduct?.sampleUrl) {
          samplePath = undefined;
          sampleFileName = undefined;
          sampleFileSize = undefined;
        }
      }

      const status = String(formData.get("status") ?? "draft") as ProductStatus;
      const originalPriceValue = Number(formData.get("originalPrice") || 0);
      const pageCountValue = Number(formData.get("pageCount") || 0);
      const selectedCategories = formData.getAll("categories").map(String).filter(Boolean);
      const categories = selectedCategories.length > 0 ? selectedCategories : ["ingles-general"];
      const primaryCategory = categories[0];
      const now = firestore.serverTimestamp();
      const productData = {
        slug: slugify(customSlug || title),
        title,
        subtitle: String(formData.get("subtitle") ?? "").trim(),
        description: String(formData.get("description") ?? "").trim(),
        author: String(formData.get("author") ?? "").trim(),
        category: primaryCategory,
        categories,
        searchTerms: buildSearchTerms(
          title,
          String(formData.get("subtitle") ?? "").trim(),
          String(formData.get("description") ?? "").trim(),
          categories.join(" "),
          String(formData.get("author") ?? "").trim(),
        ),
        level: String(formData.get("level") ?? "all"),
        price: Number(formData.get("price") || 0),
        originalPrice: originalPriceValue > 0 ? originalPriceValue : null,
        currency: String(formData.get("currency") ?? "COP"),
        format: String(formData.get("format") ?? "PDF"),
        language: String(formData.get("language") ?? "Español").trim(),
        pageCount: pageCountValue > 0 ? pageCountValue : null,
        featured: formData.get("featured") === "on",
        included: String(formData.get("included") ?? "")
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        sampleUrl: sampleUrl ?? null,
        sampleLabel: String(formData.get("sampleLabel") ?? "").trim() || null,
        samplePath: samplePath ?? null,
        sampleFileName: sampleFileName ?? null,
        sampleFileSize: sampleFileSize ?? null,
        status,
        coverImage: coverImage ?? null,
        coverPath: coverPath ?? null,
        coverThumbnail: coverThumbnail ?? null,
        coverThumbnailPath: coverThumbnailPath ?? null,
        filePath: filePath ?? null,
        fileVersionId: fileVersionId ?? null,
        fileName: fileName ?? null,
        fileSize: fileSize ?? null,
        assetVersions,
        updatedAt: now,
        createdAt: editingProduct?.createdAt ? editingProduct.createdAt : now,
        publishedAt:
          status === "published"
            ? editingProduct?.publishedAt || now
            : editingProduct?.publishedAt || null,
        updatedBy: user.uid,
      };

      await firestore.setDoc(productRef, productData, { merge: true });

      if (selectedCover && editingProduct?.coverPath && editingProduct.coverPath !== newCoverPath) {
        await storage
          .deleteObject(storage.ref(storage.getStorage(firebaseApp), editingProduct.coverPath))
          .catch(() => undefined);
      }
      if (
        selectedCover &&
        editingProduct?.coverThumbnailPath &&
        editingProduct.coverThumbnailPath !== newCoverThumbnailPath
      ) {
        await storage
          .deleteObject(
            storage.ref(storage.getStorage(firebaseApp), editingProduct.coverThumbnailPath),
          )
          .catch(() => undefined);
      }
      if (
        editingProduct?.samplePath &&
        editingProduct.samplePath !== samplePath &&
        (selectedSample || removeSample || manualSampleUrl !== editingProduct.sampleUrl)
      ) {
        await storage
          .deleteObject(storage.ref(storage.getStorage(firebaseApp), editingProduct.samplePath))
          .catch(() => undefined);
      }
      toast.success(status === "published" ? "Producto publicado." : "Borrador guardado.");
      setEditingProduct(null);
      setShowProductForm(false);
      form.reset();
    } catch (error) {
      if (newCoverPath || newCoverThumbnailPath || newFilePath || newSamplePath) {
        const storage = await import("firebase/storage");
        const bucket = storage.getStorage(firebaseApp);
        await Promise.allSettled(
          [newCoverPath, newCoverThumbnailPath, newFilePath, newSamplePath]
            .filter((path): path is string => Boolean(path))
            .map((path) => storage.deleteObject(storage.ref(bucket, path))),
        );
      }
      toast.error(error instanceof Error ? error.message : "No pudimos guardar el producto.");
    } finally {
      setSavingProduct(false);
    }
  }

  async function changeProductStatus(product: Product) {
    try {
      const { doc, getFirestore, serverTimestamp, updateDoc } = await import("firebase/firestore");
      const nextStatus: ProductStatus = product.status === "published" ? "draft" : "published";
      await updateDoc(doc(getFirestore(firebaseApp), "products", product.id), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
        ...(nextStatus === "published" && !product.publishedAt
          ? { publishedAt: serverTimestamp() }
          : {}),
      });
      toast.success(nextStatus === "published" ? "Producto publicado." : "Producto ocultado.");
    } catch {
      toast.error("No pudimos cambiar el estado del producto.");
    }
  }

  async function toggleProductFeatured(product: Product) {
    try {
      const { doc, getFirestore, serverTimestamp, updateDoc } = await import("firebase/firestore");
      await updateDoc(doc(getFirestore(firebaseApp), "products", product.id), {
        featured: !product.featured,
        updatedAt: serverTimestamp(),
      });
      toast.success(product.featured ? "Producto ya no destacado." : "Producto destacado.");
    } catch {
      toast.error("No pudimos cambiar si el producto está destacado.");
    }
  }

  async function archiveProduct(product: Product) {
    if (product.status === "archived") {
      try {
        const { doc, getFirestore, serverTimestamp, updateDoc, deleteField } =
          await import("firebase/firestore");
        await updateDoc(doc(getFirestore(firebaseApp), "products", product.id), {
          status: "draft",
          archivedAt: deleteField(),
          archivedBy: deleteField(),
          updatedAt: serverTimestamp(),
        });
        toast.success("Producto restaurado como borrador. Sus archivos se conservaron.");
      } catch {
        toast.error("No pudimos restaurar el producto.");
      }
      return;
    }

    const confirmed = window.confirm(
      `¿Archivar “${product.title}”? Dejará de venderse, pero sus archivos y las compras anteriores se conservarán.`,
    );
    if (!confirmed) return;

    try {
      const { doc, getFirestore, serverTimestamp, updateDoc } = await import("firebase/firestore");
      await updateDoc(doc(getFirestore(firebaseApp), "products", product.id), {
        status: "archived",
        archivedAt: serverTimestamp(),
        archivedBy: user?.uid ?? null,
        updatedAt: serverTimestamp(),
      });
      toast.success("Producto archivado sin borrar sus archivos.");
    } catch {
      toast.error("No pudimos archivar el producto.");
    }
  }

  async function downloadProductFile(product: Product) {
    if (!product.filePath) {
      toast.error("Este producto no tiene archivo.");
      return;
    }
    try {
      const storage = await import("firebase/storage");
      const url = await storage.getDownloadURL(
        storage.ref(storage.getStorage(firebaseApp), product.filePath),
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.download = product.fileName || "producto";
      anchor.click();
    } catch {
      toast.error("No pudimos descargar el archivo.");
    }
  }

  async function runContactAction(
    action: "archive" | "unarchive" | "delete",
    contact: MailchimpContact,
  ) {
    const destructiveMessage =
      action === "delete"
        ? `¿Eliminar permanentemente a ${contact.emailAddress}? Mailchimp no permitirá volver a importarlo.`
        : action === "archive"
          ? `¿Archivar a ${contact.emailAddress}? Dejará de aparecer entre los contactos activos.`
          : `¿Desarchivar a ${contact.emailAddress}? Recuperará su estado anterior.`;
    if (!window.confirm(destructiveMessage)) return;

    setContactAction(`${action}:${contact.id}`);
    try {
      const response = await appCheckFetch("/api/admin/mailchimp", {
        method: "POST",
        headers: await getAdminHeaders(),
        body: JSON.stringify({
          action,
          subscriberHash: contact.id,
          emailAddress: contact.emailAddress,
          previousStatus: contact.status,
          name: contact.name,
        }),
      });
      const data = await readApiResponse<{ message?: string }>(response);
      if (!response.ok) throw new Error(data.message || "Mailchimp rechazó la operación.");
      toast.success(data.message || "Contacto actualizado.");
      await loadContacts(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos actualizar el contacto.");
    } finally {
      setContactAction(null);
    }
  }

  const filteredProducts = useMemo(() => {
    const search = productSearch.trim().toLowerCase();
    if (!search) return products;
    return products.filter((product) =>
      `${product.title} ${product.slug} ${getProductCategories(product).join(" ")}`
        .toLowerCase()
        .includes(search),
    );
  }, [productSearch, products]);

  const filteredContacts = useMemo(() => {
    const search = contactSearch.trim().toLowerCase();
    if (!search) return contacts;
    return contacts.filter((contact) =>
      `${contact.emailAddress} ${contact.name ?? ""} ${contact.tags.join(" ")}`
        .toLowerCase()
        .includes(search),
    );
  }, [contactSearch, contacts]);

  if (isLoading || !user || !isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <LoaderCircle className="h-6 w-6 animate-spin text-black" />
        <span className="sr-only">Verificando acceso</span>
      </div>
    );
  }

  const publishedCount = products.filter((product) => product.status === "published").length;
  const archivedCount = contactsTotalArchived;
  const subscriberCount = contacts.filter(
    (contact) => !contact.archived && contact.status === "subscribed",
  ).length;

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await logout();
      await navigate({ to: "/", replace: true });
    } catch {
      toast.error("No pudimos cerrar la sesión.");
      setLoggingOut(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-white text-black">
      <SiteHeader />

      <div className="mx-auto grid w-full max-w-7xl flex-1 lg:grid-cols-[230px_minmax(0,1fr)]">
        <aside className="border-b border-white/10 bg-black px-4 py-4 text-white lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:self-start lg:overflow-y-auto lg:border-b-0 lg:border-r lg:px-5 lg:py-7">
          <nav
            className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:mt-6 lg:grid-cols-1"
            aria-label="Administración"
          >
            <AdminNavButton
              active={section === "products"}
              icon={<Package className="h-4 w-4" />}
              label="Productos"
              onClick={() => selectSection("products")}
            />
            <AdminNavButton
              active={section === "sales"}
              icon={<ReceiptText className="h-4 w-4" />}
              label="Ventas"
              onClick={() => selectSection("sales")}
            />
            <AdminNavButton
              active={section === "tickets"}
              icon={<LifeBuoy className="h-4 w-4" />}
              label="Soporte"
              onClick={() => {
                setSupportQuery("");
                selectSection("tickets");
              }}
            />
            <AdminNavButton
              active={section === "community"}
              icon={<MessageCircle className="h-4 w-4" />}
              label="Comunidad"
              onClick={() => selectSection("community")}
            />
            <AdminNavButton
              active={section === "subscribers"}
              icon={<Users className="h-4 w-4" />}
              label="Suscriptores"
              onClick={() => selectSection("subscribers")}
            />
            <AdminNavButton
              active={section === "coupons"}
              icon={<BadgePercent className="h-4 w-4" />}
              label="Cupones"
              onClick={() => selectSection("coupons")}
            />
          </nav>
          <div className="mt-3 grid grid-cols-2 gap-2 lg:mt-4 lg:grid-cols-1">
            <AdminNavButton
              active={section === "analytics"}
              icon={<BarChart3 className="h-4 w-4" />}
              label="Analytics"
              onClick={() => selectSection("analytics")}
            />
            <Link
              to="/legal"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/20 px-3 py-2.5 text-xs font-semibold text-white/65 transition hover:bg-white hover:text-black lg:justify-start"
            >
              <Scale className="h-4 w-4" />
              Centro legal
            </Link>
          </div>
          <button
            type="button"
            disabled={loggingOut}
            onClick={() => void handleLogout()}
            className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-white/25 px-4 text-xs font-semibold text-white transition hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:opacity-50 lg:mt-6"
          >
            {loggingOut ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="h-4 w-4" />
            )}
            Cerrar sesión
          </button>
        </aside>

        <main id="main-content" className="min-w-0 px-4 py-7 sm:px-7 lg:px-10 lg:py-10">
          {section === "products" ? (
            <section>
              <AdminTitle
                eyebrow="Catálogo digital"
                title="Productos"
                description="Crea, edita y publica los recursos que aparecen en la tienda."
                action={
                  <Button
                    className="h-11"
                    onClick={() => {
                      setEditingProduct(null);
                      setShowProductForm(true);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Nuevo producto
                  </Button>
                }
              />

              <div className="mt-7 grid gap-4 sm:grid-cols-3">
                <StatCard icon={<Package />} label="Total" value={products.length} />
                <StatCard icon={<CheckCircle2 />} label="Publicados" value={publishedCount} />
                <StatCard
                  icon={<FileText />}
                  label="Borradores"
                  value={products.length - publishedCount}
                />
              </div>

              {showProductForm && (
                <div className="mt-7">
                  <ProductForm
                    key={editingProduct?.id ?? "new-product"}
                    editingProduct={editingProduct}
                    isSaving={savingProduct}
                    onCancel={() => {
                      setShowProductForm(false);
                      setEditingProduct(null);
                    }}
                    onSubmit={handleProductSubmit}
                  />
                </div>
              )}

              <div className="mt-7 rounded-xl border border-black/15 bg-white">
                <div className="flex flex-col gap-4 border-b border-black/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Biblioteca</h2>
                    <p className="mt-1 text-sm text-black/45">
                      Los borradores no son visibles en la tienda.
                    </p>
                  </div>
                  <SearchField
                    value={productSearch}
                    onChange={setProductSearch}
                    placeholder="Buscar producto"
                  />
                </div>
                {productsLoading ? (
                  <LoadingBlock label="Cargando productos…" />
                ) : filteredProducts.length === 0 ? (
                  <EmptyBlock
                    icon={<BookOpen className="h-6 w-6" />}
                    title={products.length ? "No hay coincidencias" : "Aún no hay productos"}
                    description={
                      products.length
                        ? "Prueba con otro término de búsqueda."
                        : "Crea el primer recurso digital de la tienda."
                    }
                  />
                ) : (
                  <div className="divide-y divide-black/[0.06]">
                    {filteredProducts.map((product) => (
                      <ProductRow
                        key={product.id}
                        product={product}
                        onEdit={() => {
                          setEditingProduct(product);
                          setShowProductForm(true);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        onFeatured={() => void toggleProductFeatured(product)}
                        onStatus={() => void changeProductStatus(product)}
                        onDownload={() => void downloadProductFile(product)}
                        onArchive={() => void archiveProduct(product)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          ) : section === "sales" ? (
            <AdminSalesPanel
              user={user}
              onOpenSupport={(ticketReference) => {
                setSupportQuery(ticketReference);
                selectSection("tickets");
              }}
            />
          ) : section === "analytics" ? (
            <AdminAnalyticsPanel user={user} />
          ) : section === "community" ? (
            <AdminEpisodeCommunityPanel user={user} />
          ) : section === "coupons" ? (
            <section>
              <AdminTitle
                eyebrow="Descuentos"
                title="Cupones"
                description="Consulta los códigos vinculados a cada correo y su estado actual."
                action={
                  <div className="flex gap-2">
                    <Button variant="outline" className="h-11 rounded-full" onClick={() => void loadCoupons()} disabled={couponsLoading}>
                      <RefreshCw className={`h-4 w-4 ${couponsLoading ? "animate-spin" : ""}`} />
                      Actualizar
                    </Button>
                    <Button className="h-11 rounded-full" onClick={() => setShowCouponForm((current) => !current)}>
                      <Plus className="h-4 w-4" /> Nuevo cupón
                    </Button>
                  </div>
                }
              />
              {showCouponForm && (
                <form onSubmit={createCoupon} className="mt-7 rounded-xl border border-black/15 bg-white p-5 sm:p-6">
                  <div className="grid gap-4 md:grid-cols-4">
                    <div>
                      <Label htmlFor="coupon-percent">Descuento (%)</Label>
                      <Input id="coupon-percent" name="percent" type="number" min="1" max="100" required className="mt-2" placeholder="15" />
                    </div>
                    <div>
                      <Label htmlFor="coupon-scope">Tipo</Label>
                      <select id="coupon-scope" name="scope" defaultValue="specific" className="mt-2 h-10 w-full rounded-md border border-black/15 bg-white px-3 text-sm">
                        <option value="specific">Correo específico</option>
                        <option value="general">General · cualquier correo</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="coupon-email">Correo (si es específico)</Label>
                      <Input id="coupon-email" name="email" type="email" className="mt-2" placeholder="cliente@correo.com" />
                    </div>
                    <div>
                      <Label htmlFor="coupon-expires">Caducidad</Label>
                      <Input id="coupon-expires" name="expiresAt" type="datetime-local" required className="mt-2" />
                    </div>
                  </div>
                  <div className="mt-5 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowCouponForm(false)}>Cancelar</Button>
                    <Button type="submit" disabled={couponSaving}>
                      {couponSaving && <LoaderCircle className="animate-spin" />}
                      Crear cupón
                    </Button>
                  </div>
                </form>
              )}
              <div className="mt-7 grid gap-4 sm:grid-cols-3">
                <StatCard icon={<CheckCircle2 />} label="Activos" value={coupons.filter((coupon) => coupon.status === "active").length} />
                <StatCard icon={<ReceiptText />} label="Usados" value={coupons.filter((coupon) => coupon.status === "used").length} />
                <StatCard icon={<Archive />} label="Vencidos" value={coupons.filter((coupon) => coupon.status === "expired").length} />
              </div>
              <div className="mt-7 overflow-hidden rounded-xl border border-black/15 bg-white">
                {couponsLoading && !couponsLoaded ? (
                  <LoadingBlock label="Cargando cupones…" />
                ) : coupons.length === 0 ? (
                  <EmptyBlock icon={<BadgePercent className="h-6 w-6" />} title="No hay cupones" description="Los cupones generados aparecerán aquí." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[780px] table-fixed text-left text-xs">
                      <thead className="border-b border-black/10 bg-black/[0.03] text-[11px] uppercase tracking-wider text-black/45">
                        <tr><th className="w-[29%] px-4 py-3.5">Correo</th><th className="w-[17%] px-4 py-3.5">Código</th><th className="w-[11%] px-4 py-3.5">Descuento</th><th className="w-[15%] px-4 py-3.5">Creado</th><th className="w-[16%] px-4 py-3.5">Vence / usado</th><th className="w-[12%] px-4 py-3.5">Estado</th></tr>
                      </thead>
                      <tbody className="divide-y divide-black/[0.06]">
                        {coupons.map((coupon) => (
                          <tr key={coupon.id}>
                            <td className="break-all px-4 py-3.5 font-medium">{coupon.scope === "general" ? "Cualquier correo" : coupon.email}</td>
                            <td className="px-4 py-3.5 font-mono text-[11px]"><span className="whitespace-nowrap">{coupon.code}</span></td>
                            <td className="px-4 py-3.5">{coupon.percent}%</td>
                            <td className="px-4 py-3.5 text-black/55"><CouponDateTime value={coupon.createdAt} /></td>
                            <td className="px-4 py-3.5 text-black/55"><CouponDateTime value={coupon.usedAt ?? coupon.expiresAt} /></td>
                            <td className="px-4 py-3.5"><span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${coupon.status === "active" ? "bg-emerald-100 text-emerald-800" : coupon.status === "used" ? "bg-black text-white" : "bg-black/[0.07] text-black/55"}`}>{coupon.status === "active" ? "Activo" : coupon.status === "used" ? "Usado" : "Vencido"}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          ) : section === "subscribers" ? (
            <section>
              <AdminTitle
                eyebrow="Audiencia de Mailchimp"
                title="Suscriptores"
                description="Consulta y administra los contactos de la lista conectada."
                action={
                  <Button
                    variant="outline"
                    className="h-11 rounded-full border-black/10 bg-white px-5"
                    onClick={() => void loadContacts(false)}
                    disabled={contactsLoading}
                  >
                    <RefreshCw className={`h-4 w-4 ${contactsLoading ? "animate-spin" : ""}`} />
                    Actualizar
                  </Button>
                }
              />

              <div className="mt-7 grid gap-4 sm:grid-cols-3">
                <StatCard
                  icon={<Users />}
                  label="Contactos"
                  value={contactsTotalActive + contactsTotalArchived}
                />
                <StatCard icon={<Send />} label="Suscritos" value={subscriberCount} />
                <StatCard icon={<Archive />} label="Archivados" value={archivedCount} />
              </div>

              <div className="mt-7 rounded-xl border border-black/15 bg-white">
                <div className="flex flex-col gap-4 border-b border-black/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Audiencia</h2>
                    <p className="mt-1 text-sm text-black/45">
                      Eliminar permanentemente impide volver a importar ese contacto.
                    </p>
                  </div>
                  <SearchField
                    value={contactSearch}
                    onChange={setContactSearch}
                    placeholder="Correo o nombre"
                  />
                </div>
                {contactsLoading ? (
                  <LoadingBlock label="Consultando Mailchimp…" />
                ) : !contactsLoaded ? (
                  <EmptyBlock
                    icon={<Mail className="h-6 w-6" />}
                    title="Conecta con tu audiencia"
                    description="Pulsa Actualizar para consultar los contactos de Mailchimp."
                  />
                ) : filteredContacts.length === 0 ? (
                  <EmptyBlock
                    icon={<Users className="h-6 w-6" />}
                    title={contacts.length ? "No hay coincidencias" : "No hay contactos"}
                    description={
                      contacts.length
                        ? "Prueba con otro correo, nombre o etiqueta."
                        : "Los nuevos suscriptores aparecerán aquí."
                    }
                  />
                ) : (
                  <div className="divide-y divide-black/[0.06]">
                    {filteredContacts.map((contact) => (
                      <ContactRow
                        key={`${contact.archived ? "archived" : "active"}:${contact.id}`}
                        contact={contact}
                        pendingAction={contactAction}
                        onAction={(action) => void runContactAction(action, contact)}
                      />
                    ))}
                    {contactsHasMore && (
                      <div className="p-5 text-center">
                        <Button
                          type="button"
                          variant="outline"
                          disabled={contactsLoadingMore}
                          onClick={() => void loadContacts(true)}
                        >
                          {contactsLoadingMore && <LoaderCircle className="animate-spin" />}
                          {contactsLoadingMore ? "Cargando…" : "Cargar más contactos"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          ) : (
            <AdminTicketsPanel user={user} initialQuery={supportQuery} />
          )}
        </main>
      </div>
      <SiteFooter />
    </div>
  );
}

interface AnalyticsData {
  configured: boolean;
  periodDays?: number;
  message?: string;
  totals?: {
    views: number;
    users: number;
    productViews: number;
    addToCarts: number;
    checkouts: number;
    purchases: number;
    downloads: number;
    sessions: number;
    newUsers: number;
    engagementMinutes: number;
  };
  daily?: Array<{ date: string; views: number; users: number }>;
  pages?: Array<{ path: string; views: number }>;
  countries?: Array<{ name: string; users: number; views: number }>;
  cities?: Array<{ name: string; country: string; users: number }>;
  devices?: Array<{ name: string; users: number }>;
  sources?: Array<{ name: string; campaign: string; users: number; purchases: number }>;
  products?: Array<{ id: string; name: string; views: number; carts: number; downloads: number; purchases: number; revenue: number }>;
  technologies?: Array<{ name: string; users: number }>;
  hours?: Array<{ hour: number; weekday: string; views: number; users: number }>;
  searches?: Array<{ term: string; searches: number; avgResults: number }>;
  podcast?: Array<{ episode: string; clicks: number; subscriptions: number; platforms: string }>;
  errors?: Array<{ path: string; kind: string; occurrences: number }>;
  conversions?: Array<{ name: string; users: number; purchases: number }>;
}

function AdminAnalyticsPanel({ user }: { user: { getIdToken: () => Promise<string> } }) {
  const [data, setData] = useState<AnalyticsData>();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<7 | 30 | 90>(30);
  const [question, setQuestion] = useState("");
  const [chat, setChat] = useState<Array<{ role: "user" | "assistant"; text: string }>>([]);
  const [asking, setAsking] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  async function loadAnalytics() {
    setLoading(true);
    try {
      const response = await appCheckFetch(`/api/admin/analytics?days=${period}`, {
        headers: { "X-Firebase-Auth": `Bearer ${await user.getIdToken()}` },
      });
      const result = await readApiResponse<AnalyticsData>(response);
      if (!response.ok) throw new Error(result.message || "No pudimos cargar Analytics.");
      setData(result);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos cargar Analytics.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAnalytics();
  }, [period]);

  async function askAnalytics(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextQuestion = question.trim();
    if (!nextQuestion || !data || asking) return;
    setQuestion("");
    setChat((current) => [...current, { role: "user", text: nextQuestion }]);
    setAsking(true);
    try {
      const response = await appCheckFetch("/api/admin/analytics", {
        method: "POST",
        headers: { "X-Firebase-Auth": `Bearer ${await user.getIdToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ question: nextQuestion, context: data }),
      });
      const result = await readApiResponse<{ answer?: string; message?: string }>(response);
      if (!response.ok) throw new Error(result.message || "No pudimos consultar a Gemini.");
      setChat((current) => [...current, { role: "assistant", text: result.answer || "No encontré una respuesta." }]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos consultar a Gemini.");
    } finally {
      setAsking(false);
    }
  }

  const totals = data?.totals;
  const daily = data?.daily ?? [];
  const maxViews = Math.max(1, ...daily.map((item) => item.views));
  const periodViews = daily.reduce((sum, item) => sum + item.views, 0);
  const activeDays = daily.filter((item) => item.views > 0).length;
  const conversion = totals?.productViews
    ? ((totals.purchases / totals.productViews) * 100).toFixed(1)
    : "0.0";

  return (
    <section>
      <AdminTitle
        eyebrow={`Últimos ${period} días`}
        title="Analytics"
        description="Tráfico, contenido y recorrido de compra, sin salir del panel de administración."
        action={
          <Button variant="outline" className="h-11 rounded-full" onClick={() => void loadAnalytics()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        }
      />
      <div className="mt-5 flex flex-wrap items-center gap-2" aria-label="Período de Analytics">
        <span className="mr-2 text-xs font-semibold text-black/45">Período</span>
        {([7, 30, 90] as const).map((days) => <button key={days} type="button" onClick={() => setPeriod(days)} className={`h-9 rounded-full border px-4 text-xs font-semibold transition ${period === days ? "border-black bg-black text-white" : "border-black/15 bg-white hover:border-black"}`}>{days} días</button>)}
      </div>
      {loading && !data ? (
        <div className="mt-7 rounded-xl border border-black/15"><LoadingBlock label="Consultando Analytics…" /></div>
      ) : !data?.configured || !totals ? (
        <div className="mt-7 rounded-xl border border-black/15 bg-black/[0.02] p-7">
          <BarChart3 className="h-8 w-8" />
          <h2 className="mt-5 text-xl font-semibold">Falta conectar la fuente de datos</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-black/55">{data?.message || "No encontramos datos de Analytics disponibles."}</p>
          <a href="https://console.firebase.google.com/project/socrattica/settings/integrations/bigquery" target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold underline underline-offset-4">
            Configurar exportación <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      ) : (
        <>
          <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <AnalyticsStat icon={<Eye />} label="Vistas" value={totals.views} detail={`${totals.users.toLocaleString("es-CO")} usuarios`} />
            <AnalyticsStat icon={<ShoppingCart />} label="Añadidos al carrito" value={totals.addToCarts} detail={`${totals.checkouts.toLocaleString("es-CO")} iniciaron pago`} />
            <AnalyticsStat icon={<ReceiptText />} label="Compras" value={totals.purchases} detail={`${conversion}% desde producto`}  />
            <AnalyticsStat icon={<Download />} label="Descargas" value={totals.downloads} detail={`${totals.productViews.toLocaleString("es-CO")} vistas de producto`} />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <AnalyticsInsight label="Sesiones" value={totals.sessions} detail={`${totals.newUsers.toLocaleString("es-CO")} usuarios nuevos`} />
            <AnalyticsInsight label="Interacción" value={`${totals.engagementMinutes.toLocaleString("es-CO")} min`} detail="Tiempo activo acumulado" />
            <AnalyticsInsight label="Vistas por usuario" value={(totals.views / Math.max(1, totals.users)).toFixed(1)} detail="Promedio del período" />
          </div>
          <div className="mt-7 grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(420px,1.35fr)]">
            <div className="rounded-xl border border-black/15 p-5 sm:p-7">
              <div className="flex items-start justify-between gap-4">
                <div><h2 className="text-lg font-semibold">Actividad diaria</h2><p className="mt-1 text-xs text-black/45">Vistas de página por día</p></div>
                <div className="text-right"><p className="text-2xl font-semibold tabular-nums">{periodViews.toLocaleString("es-CO")}</p><p className="text-[10px] text-black/40">vistas · {activeDays} días activos</p></div>
              </div>
              <div className="relative mt-8 h-48" aria-label="Gráfico de vistas diarias">
                <div className="pointer-events-none absolute inset-0 flex flex-col justify-between" aria-hidden="true">
                  {[100, 75, 50, 25, 0].map((level) => <div key={level} className="flex items-center gap-2"><span className="w-6 text-right text-[9px] tabular-nums text-black/30">{Math.round((maxViews * level) / 100)}</span><span className="h-px flex-1 bg-black/[0.07]" /></div>)}
                </div>
                <div className="absolute inset-y-0 left-8 right-0 flex items-end gap-1">
                {daily.map((item) => (
                  <div key={item.date} className="group relative flex h-full min-w-0 flex-1 items-end">
                    <div className={`w-full rounded-t-sm transition ${item.views ? "min-h-1 bg-black/75 hover:bg-black" : "h-px bg-black/15"}`} style={item.views ? { height: `${(item.views / maxViews) * 100}%` } : undefined} />
                    <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1.5 text-[10px] text-white shadow-lg group-hover:block">{new Date(`${item.date.slice(0, 4)}-${item.date.slice(4, 6)}-${item.date.slice(6, 8)}T12:00:00`).toLocaleDateString("es-CO", { day: "numeric", month: "short" })} · {item.views} vistas · {item.users} usuarios</span>
                  </div>
                ))}
                </div>
              </div>
              <div className="ml-8 mt-3 flex justify-between text-[10px] text-black/40"><span>Hace 30 días</span><span>15 días</span><span>Hoy</span></div>
            </div>
            <div className="rounded-xl border border-black/15 p-5 sm:p-7">
              <h2 className="text-lg font-semibold">Páginas principales</h2>
              <p className="mt-1 text-xs text-black/45">Ordenadas por vistas</p>
              <div className="mt-5 divide-y divide-black/[0.06]">
                {(data.pages ?? []).map((page, index) => (
                  <div key={`${page.path}-${index}`} className="flex items-center gap-3 py-3">
                    <span className="w-5 text-xs font-semibold text-black/30">{index + 1}</span>
                    <span className="min-w-0 flex-1 truncate text-sm" title={page.path}>{page.path || "/"}</span>
                    <span className="text-sm font-semibold tabular-nums">{page.views.toLocaleString("es-CO")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
            <AnalyticsRanking icon={<Globe2 />} title="Países" subtitle="Usuarios y vistas" items={(data.countries ?? []).map((item) => ({ label: item.name, value: item.users, detail: `${item.views.toLocaleString("es-CO")} vistas` }))} />
            <AnalyticsRanking icon={<MapPin />} title="Ciudades" subtitle="Ubicación aproximada" items={(data.cities ?? []).map((item) => ({ label: item.name, value: item.users, detail: item.country }))} />
            <AnalyticsRanking icon={<Smartphone />} title="Dispositivos" subtitle="Usuarios por categoría" items={(data.devices ?? []).map((item) => ({ label: item.name, value: item.users }))} />
            <AnalyticsRanking icon={<BarChart3 />} title="Adquisición" subtitle="Origen del tráfico" items={(data.sources ?? []).map((item) => ({ label: item.name, value: item.users }))} />
          </div>
          <div className="mt-8 rounded-xl border border-black/15 p-5 sm:p-7">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-widest text-black/35">Conversión</p><h2 className="mt-1 text-xl font-semibold">Embudo de compra</h2></div><p className="text-xs text-black/45">De la visita al producto hasta la compra</p></div>
            <div className="mt-6 grid gap-3 sm:grid-cols-4">
              {[
                { label: "Vieron producto", value: totals.productViews },
                { label: "Añadieron al carrito", value: totals.addToCarts },
                { label: "Iniciaron pago", value: totals.checkouts },
                { label: "Compraron", value: totals.purchases },
              ].map((step, index, steps) => <div key={step.label} className="relative rounded-xl bg-black/[0.03] p-4"><p className="text-xs text-black/45">{index + 1}. {step.label}</p><p className="mt-2 text-2xl font-semibold tabular-nums">{step.value.toLocaleString("es-CO")}</p>{index > 0 && <p className="mt-1 text-[10px] text-black/40">{steps[index - 1].value ? ((step.value / steps[index - 1].value) * 100).toFixed(1) : "0.0"}% avanzó</p>}</div>)}
            </div>
          </div>

          <AnalyticsSection title="Productos y ventas" description="Vistas, carritos y descargas de Analytics; compras e ingresos confirmados por la pasarela.">
            <AnalyticsDataTable headers={["Producto", "Vistas", "Carritos", "Compras", "Descargas", "Ingresos"]} rows={(data.products ?? []).map((item) => [item.name, item.views, item.carts, item.purchases, item.downloads, `$${item.revenue.toLocaleString("es-CO")}`])} />
          </AnalyticsSection>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <AnalyticsSection title="Fuentes y campañas" description="Origen, medio, campaña y compras."><AnalyticsDataTable headers={["Fuente / medio", "Campaña", "Usuarios", "Compras"]} rows={(data.sources ?? []).map((item) => [item.name, item.campaign || "(not set)", item.users, item.purchases ?? 0])} /></AnalyticsSection>
            <AnalyticsSection title="Usuarios nuevos y recurrentes" description="Composición de la audiencia del período."><div className="mt-6 grid grid-cols-2 gap-4"><AnalyticsInsight label="Nuevos" value={totals.newUsers} detail={`${((totals.newUsers / Math.max(1, totals.users)) * 100).toFixed(1)}% de usuarios`} /><AnalyticsInsight label="Recurrentes" value={Math.max(0, totals.users - totals.newUsers)} detail={`${((Math.max(0, totals.users - totals.newUsers) / Math.max(1, totals.users)) * 100).toFixed(1)}% de usuarios`} /></div></AnalyticsSection>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <AnalyticsSection title="Navegadores y sistemas" description="Combinaciones tecnológicas más utilizadas."><AnalyticsDataTable headers={["Tecnología", "Usuarios"]} rows={(data.technologies ?? []).map((item) => [item.name, item.users])} /></AnalyticsSection>
            <AnalyticsSection title="Horarios con más tráfico" description="Hora de Colombia y día de la semana."><AnalyticsDataTable headers={["Hora", "Día", "Vistas", "Usuarios"]} rows={(data.hours ?? []).map((item) => [`${String(item.hour).padStart(2, "0")}:00`, item.weekday, item.views, item.users])} /></AnalyticsSection>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <AnalyticsSection title="Búsquedas del catálogo" description="Términos buscados y resultados disponibles."><AnalyticsDataTable headers={["Búsqueda", "Veces", "Resultados promedio"]} rows={(data.searches ?? []).map((item) => [item.term, item.searches, item.avgResults])} /></AnalyticsSection>
            <AnalyticsSection title="Actividad del pódcast" description="Clics hacia plataformas y suscripciones."><AnalyticsDataTable headers={["Episodio", "Clics", "Suscripciones", "Plataformas"]} rows={(data.podcast ?? []).map((item) => [item.episode, item.clicks, item.subscriptions, item.platforms || "—"])} /></AnalyticsSection>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <AnalyticsSection title="Errores y páginas no encontradas" description="Eventos exception y page_not_found registrados."><AnalyticsDataTable headers={["Ruta", "Tipo", "Casos"]} rows={(data.errors ?? []).map((item) => [item.path, item.kind, item.occurrences])} /></AnalyticsSection>
            <AnalyticsSection title="Conversión por ubicación y dispositivo" description="Segmentos que registraron compras."><AnalyticsDataTable headers={["País · ciudad · dispositivo", "Usuarios", "Compras", "Conversión"]} rows={(data.conversions ?? []).map((item) => [item.name, item.users, item.purchases, `${((item.purchases / Math.max(1, item.users)) * 100).toFixed(1)}%`])} /></AnalyticsSection>
          </div>

          {!chatOpen && <button type="button" onClick={() => setChatOpen(true)} className="fixed bottom-5 right-5 z-50 flex h-14 items-center gap-3 rounded-full border border-white/20 bg-black px-5 text-sm font-semibold text-white shadow-[0_12px_40px_rgb(0,0,0,0.3)] transition hover:-translate-y-0.5 hover:bg-black/85"><Bot className="h-5 w-5" />Preguntar a Analytics</button>}
          {chatOpen && <aside className="fixed inset-x-3 bottom-3 z-50 flex max-h-[min(640px,calc(100vh-24px))] flex-col overflow-hidden rounded-2xl border border-white/15 bg-black text-white shadow-[0_24px_90px_rgb(0,0,0,0.45)] sm:inset-x-auto sm:bottom-5 sm:right-5 sm:h-[600px] sm:w-[420px]">
            <header className="flex items-start gap-3 border-b border-white/10 p-4"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-black"><Bot className="h-4 w-4" /></span><div className="min-w-0 flex-1"><h2 className="text-sm font-semibold">Asistente de Analytics</h2><p className="mt-0.5 text-[10px] text-white/50">Analizando los últimos {period} días</p></div><button type="button" onClick={() => setChatOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white" aria-label="Cerrar asistente"><X className="h-4 w-4" /></button></header>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
              {chat.length === 0 && <div className="rounded-xl bg-white/[0.07] p-4"><p className="text-sm font-medium">¿Qué quieres saber?</p><p className="mt-1 text-xs leading-relaxed text-white/55">Puedo comparar productos, explicar el embudo, identificar horarios fuertes o sugerir mejoras según los datos.</p><div className="mt-4 flex flex-wrap gap-2">{["¿Qué debo mejorar?", "¿Cuál producto convierte mejor?", "Resume el tráfico"].map((suggestion) => <button key={suggestion} type="button" onClick={() => setQuestion(suggestion)} className="rounded-full border border-white/15 px-3 py-2 text-[10px] text-white/70 transition hover:bg-white hover:text-black">{suggestion}</button>)}</div></div>}
              {chat.map((message, index) => <div key={index} className={`whitespace-pre-wrap rounded-xl px-4 py-3 text-xs leading-relaxed ${message.role === "user" ? "ml-8 bg-white text-black" : "mr-8 bg-white/10 text-white/85"}`}>{message.text}</div>)}
              {asking && <div className="mr-8 flex items-center gap-2 rounded-xl bg-white/10 px-4 py-3 text-xs text-white/55"><LoaderCircle className="h-3.5 w-3.5 animate-spin" />Analizando los datos…</div>}
            </div>
            <form onSubmit={askAnalytics} className="flex gap-2 border-t border-white/10 bg-black p-3"><Input value={question} onChange={(event) => setQuestion(event.target.value)} maxLength={600} autoFocus placeholder="Haz una pregunta…" className="h-11 min-w-0 border-white/15 bg-white text-black" /><Button type="submit" disabled={!question.trim() || asking} className="h-11 w-11 shrink-0 border-white bg-white p-0 text-black hover:bg-white/85" aria-label="Enviar pregunta"><Send className="h-4 w-4" /></Button></form>
          </aside>}
        </>
      )}
    </section>
  );
}

function AnalyticsSection({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return <section className="rounded-xl border border-black/15 p-5 sm:p-7"><h2 className="text-lg font-semibold">{title}</h2><p className="mt-1 text-xs text-black/45">{description}</p>{children}</section>;
}

function AnalyticsDataTable({ headers, rows }: { headers: string[]; rows: Array<Array<string | number>> }) {
  return <div className="mt-5 overflow-x-auto"><table className="w-full min-w-[520px] text-left text-xs"><thead><tr className="border-b border-black/10 text-[10px] uppercase tracking-wider text-black/35">{headers.map((header) => <th key={header} className="px-2 py-3 font-semibold first:pl-0">{header}</th>)}</tr></thead><tbody className="divide-y divide-black/[0.06]">{rows.length ? rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex} className={`px-2 py-3 ${cellIndex === 0 ? "max-w-64 truncate pl-0 font-medium" : "tabular-nums"}`} title={String(cell)}>{typeof cell === "number" ? cell.toLocaleString("es-CO") : cell}</td>)}</tr>) : <tr><td colSpan={headers.length} className="py-8 text-center text-black/40">Sin datos en este período</td></tr>}</tbody></table></div>;
}

function AnalyticsInsight({ label, value, detail }: { label: string; value: number | string; detail: string }) {
  return <div className="rounded-xl border border-black/10 bg-black/[0.02] px-5 py-4"><p className="text-[10px] font-semibold uppercase tracking-widest text-black/40">{label}</p><p className="mt-2 text-2xl font-semibold tabular-nums">{typeof value === "number" ? value.toLocaleString("es-CO") : value}</p><p className="mt-1 text-xs text-black/45">{detail}</p></div>;
}

function AnalyticsRanking({ icon, title, subtitle, items }: { icon: React.ReactNode; title: string; subtitle: string; items: Array<{ label: string; value: number; detail?: string }> }) {
  const max = Math.max(1, ...items.map((item) => item.value));
  return <div className="rounded-xl border border-black/15 p-5"><div className="flex items-start gap-3"><span className="mt-0.5 [&>svg]:h-4 [&>svg]:w-4">{icon}</span><div><h2 className="text-base font-semibold">{title}</h2><p className="mt-0.5 text-xs text-black/45">{subtitle}</p></div></div><div className="mt-5 space-y-4">{items.length ? items.slice(0, 6).map((item, index) => <div key={`${item.label}-${index}`}><div className="flex items-baseline gap-2 text-xs"><span className="min-w-0 flex-1 truncate font-medium" title={item.label}>{item.label}</span>{item.detail && <span className="truncate text-[10px] text-black/40">{item.detail}</span>}<span className="font-semibold tabular-nums">{item.value.toLocaleString("es-CO")}</span></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-black/10"><div className="h-full rounded-full bg-black" style={{ width: `${(item.value / max) * 100}%` }} /></div></div>) : <p className="py-6 text-center text-xs text-black/40">Sin datos en este período</p>}</div></div>;
}

function AnalyticsStat({ icon, label, value, detail, accent = false }: { icon: React.ReactNode; label: string; value: number; detail: string; accent?: boolean }) {
  return <div className={`rounded-xl border p-5 ${accent ? "border-black bg-black text-white" : "border-black/15 bg-white"}`}>
    <div className="flex items-center justify-between"><span className={accent ? "text-white/70" : "text-black"}>{icon}</span><span className={`text-[10px] font-semibold uppercase tracking-widest ${accent ? "text-white/50" : "text-black/35"}`}>{label}</span></div>
    <p className="mt-5 text-3xl font-semibold tracking-tight">{value.toLocaleString("es-CO")}</p>
    <p className={`mt-1 text-xs ${accent ? "text-white/55" : "text-black/45"}`}>{detail}</p>
  </div>;
}

function AdminNavButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex cursor-pointer items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold transition ${
        active
          ? "bg-white text-black shadow-sm"
          : "text-white/55 hover:bg-white/10 hover:text-white"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function AdminTitle({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45">
          <LayoutDashboard className="h-3.5 w-3.5" />
          {eyebrow}
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-[-0.05em] sm:text-4xl">{title}</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-black/50">{description}</p>
      </div>
      {action}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  accent = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-5 ${
        accent ? "border-black bg-black text-white" : "border-black/15 bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className={accent ? "text-white/70" : "text-black"}>{icon}</span>
        <span
          className={`text-xs font-semibold uppercase tracking-widest ${accent ? "text-white/55" : "text-black/35"}`}
        >
          {label}
        </span>
      </div>
      <p className="mt-5 text-3xl font-semibold tracking-[-0.04em]">{value}</p>
    </div>
  );
}

function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-full sm:w-64">
      <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-black/30" />
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-10 rounded-full border-black/10 bg-black/[0.025] pl-10 shadow-none"
      />
    </div>
  );
}

function ProductForm({ editingProduct, isSaving, onCancel, onSubmit }: ProductFormProps) {
  const [title, setTitle] = useState(editingProduct?.title ?? "");
  const [slug, setSlug] = useState(editingProduct?.slug ?? "");

  return (
    <form
      onSubmit={onSubmit}
      className="overflow-hidden rounded-xl border border-black/15 bg-white"
    >
      <div className="flex items-center justify-between border-b border-black/[0.06] p-5 sm:px-7">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-black/45">
            {editingProduct ? "Editar producto" : "Nuevo producto"}
          </p>
          <h2 className="mt-1 text-xl font-semibold">
            {editingProduct?.title || "Información del recurso"}
          </h2>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={onCancel}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Cerrar formulario</span>
        </Button>
      </div>

      <div className="p-5 sm:p-7">
        <Tabs defaultValue="details">
          <TabsList className="grid h-11 w-full max-w-md grid-cols-2 rounded-full bg-black/[0.05] p-1">
            <TabsTrigger value="details" className="rounded-full">
              Información
            </TabsTrigger>
            <TabsTrigger value="files" className="rounded-full">
              Archivos y publicación
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" forceMount className="mt-7 data-[state=inactive]:hidden">
            <div className="grid gap-5 md:grid-cols-2">
              <FormField label="Nombre" htmlFor="product-title">
                <Input
                  id="product-title"
                  name="title"
                  value={title}
                  onChange={(event) => {
                    setTitle(event.target.value);
                    if (!editingProduct) setSlug(slugify(event.target.value));
                  }}
                  placeholder="Ej. Guía de inglés B1"
                  required
                />
              </FormField>
              <FormField label="URL del producto" htmlFor="product-slug">
                <Input
                  id="product-slug"
                  name="slug"
                  value={slug}
                  onChange={(event) => setSlug(slugify(event.target.value))}
                  placeholder={slugify(title) || "guia-de-ingles-b1"}
                  required
                />
              </FormField>
              <FormField label="Subtítulo" htmlFor="product-subtitle">
                <Input
                  id="product-subtitle"
                  name="subtitle"
                  defaultValue={editingProduct?.subtitle}
                  placeholder="Una frase breve para la tarjeta"
                />
              </FormField>
              <FormField label="Autor" htmlFor="product-author">
                <Input
                  id="product-author"
                  name="author"
                  defaultValue={editingProduct?.author ?? "Socrattica"}
                  placeholder="Socrattica"
                />
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Descripción" htmlFor="product-description">
                  <Textarea
                    id="product-description"
                    name="description"
                    defaultValue={editingProduct?.description}
                    placeholder="Explica qué aprenderá la persona y para quién es este recurso."
                    className="min-h-32"
                    required
                  />
                </FormField>
              </div>
              <FormField label="Categorías" htmlFor="product-category-0">
                <div className="grid gap-2 rounded-md border border-black/20 p-3 sm:grid-cols-2">
                  {CATEGORIES.map((category, index) => {
                    const selected = editingProduct
                      ? getProductCategories(editingProduct).includes(category.slug)
                      : category.slug === "ingles-general";
                    return (
                      <label
                        key={category.slug}
                        htmlFor={`product-category-${index}`}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-black/5"
                      >
                        <input
                          id={`product-category-${index}`}
                          type="checkbox"
                          name="categories"
                          value={category.slug}
                          defaultChecked={selected}
                          className="h-4 w-4 accent-black"
                        />
                        {category.name}
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-black/55">
                  Puedes seleccionar varias. La primera seleccionada será la categoría principal.
                </p>
              </FormField>
              <FormField label="Nivel" htmlFor="product-level">
                <NativeSelect
                  id="product-level"
                  name="level"
                  defaultValue={editingProduct?.level ?? "all"}
                >
                  {LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level === "all" ? "Todos los niveles" : level}
                    </option>
                  ))}
                </NativeSelect>
              </FormField>
              <FormField label="Precio" htmlFor="product-price">
                <Input
                  id="product-price"
                  name="price"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={editingProduct?.price ?? 0}
                  required
                />
              </FormField>
              <FormField label="Precio anterior (opcional)" htmlFor="product-original-price">
                <Input
                  id="product-original-price"
                  name="originalPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={editingProduct?.originalPrice}
                />
              </FormField>
              <FormField label="Moneda" htmlFor="product-currency">
                <NativeSelect
                  id="product-currency"
                  name="currency"
                  defaultValue={editingProduct?.currency ?? "COP"}
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency}>{currency}</option>
                  ))}
                </NativeSelect>
              </FormField>
              <FormField label="Idioma" htmlFor="product-language">
                <Input
                  id="product-language"
                  name="language"
                  defaultValue={editingProduct?.language ?? "Español"}
                />
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Qué incluye (un elemento por línea)" htmlFor="product-included">
                  <Textarea
                    id="product-included"
                    name="included"
                    defaultValue={editingProduct?.included?.join("\n")}
                    placeholder={
                      "Guía principal en PDF\nEjercicios prácticos\nPlantillas descargables"
                    }
                    className="min-h-28"
                  />
                </FormField>
              </div>
              <FormField label="Enlace externo de muestra (opcional)" htmlFor="product-sample-url">
                <Input
                  id="product-sample-url"
                  name="sampleUrl"
                  type="url"
                  defaultValue={editingProduct?.sampleUrl}
                  placeholder="Úsalo solo si la muestra está alojada fuera de Socrattica"
                />
              </FormField>
              <FormField label="Nombre de la muestra" htmlFor="product-sample-label">
                <Input
                  id="product-sample-label"
                  name="sampleLabel"
                  defaultValue={editingProduct?.sampleLabel}
                  placeholder="Ver páginas de ejemplo"
                />
              </FormField>
            </div>
          </TabsContent>

          <TabsContent value="files" forceMount className="mt-7 data-[state=inactive]:hidden">
            <div className="grid gap-5 md:grid-cols-2">
              <FormField label="Formato" htmlFor="product-format">
                <NativeSelect
                  id="product-format"
                  name="format"
                  defaultValue={editingProduct?.format ?? "PDF"}
                >
                  {FORMATS.map((format) => (
                    <option key={format}>{format}</option>
                  ))}
                </NativeSelect>
              </FormField>
              <FormField label="Número de páginas" htmlFor="product-pages">
                <Input
                  id="product-pages"
                  name="pageCount"
                  type="number"
                  min="1"
                  defaultValue={editingProduct?.pageCount}
                />
              </FormField>
              <FormField label="Portada" htmlFor="product-cover">
                <label className="flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-black/20 bg-white px-4 text-center transition hover:border-black">
                  <UploadCloud className="h-6 w-6 text-black" />
                  <span className="mt-2 text-sm font-semibold">
                    {editingProduct?.coverImage ? "Reemplazar portada" : "Subir portada"}
                  </span>
                  <span className="mt-1 text-xs text-black/40">JPG, PNG o WebP · máx. 10 MB</span>
                  <input
                    id="product-cover"
                    name="cover"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                  />
                </label>
              </FormField>
              <FormField label="Archivo digital" htmlFor="product-file">
                <label className="flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-black/20 bg-white px-4 text-center transition hover:border-black">
                  <FileText className="h-6 w-6 text-black" />
                  <span className="mt-2 max-w-full truncate text-sm font-semibold">
                    {editingProduct?.fileName || "Subir archivo"}
                  </span>
                  <span className="mt-1 text-xs text-black/40">
                    PDF, EPUB, ZIP o audio · máx. 200 MB
                  </span>
                  <input
                    id="product-file"
                    name="digitalFile"
                    type="file"
                    accept=".pdf,.epub,.zip,.mp3,.m4a,.wav,application/pdf,application/epub+zip,application/zip,audio/*"
                    className="sr-only"
                  />
                </label>
              </FormField>
              <FormField label="PDF de muestra pública" htmlFor="product-sample-file">
                <label className="flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-black/20 bg-white px-4 text-center transition hover:border-black">
                  <FileText className="h-6 w-6 text-black" />
                  <span className="mt-2 max-w-full truncate text-sm font-semibold">
                    {editingProduct?.sampleFileName || "Subir muestra PDF"}
                  </span>
                  <span className="mt-1 text-xs text-black/50">
                    Vista previa pública · PDF · máx. 30 MB
                  </span>
                  {editingProduct?.sampleFileSize && (
                    <span className="mt-1 text-xs text-black/50">
                      Archivo actual: {formatBytes(editingProduct.sampleFileSize)}
                    </span>
                  )}
                  <input
                    id="product-sample-file"
                    name="sampleFile"
                    type="file"
                    accept=".pdf,application/pdf"
                    className="sr-only"
                  />
                </label>
              </FormField>
              {editingProduct?.sampleUrl && (
                <div className="flex items-end pb-2">
                  <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
                    <input name="removeSample" type="checkbox" className="h-4 w-4 accent-black" />
                    Quitar la muestra actual
                  </label>
                </div>
              )}
              <FormField label="Estado" htmlFor="product-status">
                <NativeSelect
                  id="product-status"
                  name="status"
                  defaultValue={editingProduct?.status ?? "draft"}
                >
                  <option value="draft">Borrador — oculto en la tienda</option>
                  <option value="published">Publicado — visible en la tienda</option>
                </NativeSelect>
              </FormField>
              <div className="flex items-end pb-2">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
                  <input
                    name="featured"
                    type="checkbox"
                    defaultChecked={editingProduct?.featured}
                    className="h-4 w-4 accent-black"
                  />
                  Destacar este producto
                </label>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-7 flex flex-col-reverse gap-3 border-t border-black/[0.06] pt-6 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-full" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSaving} className="px-6">
            {isSaving ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            {isSaving ? "Guardando…" : editingProduct ? "Guardar cambios" : "Crear producto"}
          </Button>
        </div>
      </div>
    </form>
  );
}

function FormField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label
        htmlFor={htmlFor}
        className="mb-2 block text-xs font-semibold uppercase tracking-widest text-black/45"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}

function NativeSelect({ className = "", ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={`flex h-10 w-full rounded-lg border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-black focus:ring-2 focus:ring-black/10 ${className}`}
      {...props}
    />
  );
}

function ProductRow({
  product,
  onEdit,
  onFeatured,
  onStatus,
  onDownload,
  onArchive,
}: {
  product: Product;
  onEdit: () => void;
  onFeatured: () => void;
  onStatus: () => void;
  onDownload: () => void;
  onArchive: () => void;
}) {
  const { formatPrice } = useCurrency();

  return (
    <article className="flex flex-col gap-4 p-5 transition hover:bg-black/[0.012] sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <div className="flex h-20 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-black text-white">
          {product.coverThumbnail || product.coverImage ? (
            <img
              src={product.coverThumbnail ?? product.coverImage}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <BookOpen className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-display text-xl">{product.title}</h3>
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                product.status === "published"
                  ? "bg-black text-white"
                  : product.status === "archived"
                    ? "border border-black bg-black/10 text-black/60"
                    : "border border-black/15 bg-white text-black/60"
              }`}
            >
              {product.status === "published"
                ? "Publicado"
                : product.status === "archived"
                  ? "Archivado"
                  : "Borrador"}
            </span>
          </div>
          <p className="mt-1 truncate text-xs text-black/40">
            {getProductCategories(product)
              .map((category) => category.replace(/-/g, " "))
              .join(" · ")} · {formatPrice(product.price, product.currency)}
          </p>
          <p className="mt-1 truncate text-xs text-black/35">
            {product.fileName || "Sin archivo"} · {formatBytes(product.fileSize)}
          </p>
          <p className="mt-1 truncate text-xs text-black/45">
            {product.sampleUrl
              ? `Muestra: ${product.sampleFileName || "enlace externo"}`
              : "Sin muestra pública"}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 sm:justify-end">
        <IconButton label="Editar" icon={<Pencil />} onClick={onEdit} />
        <IconButton
          label="Descargar"
          icon={<Download />}
          onClick={onDownload}
          disabled={!product.filePath}
        />
        {product.status !== "archived" && (
          <IconButton
            label={product.status === "published" ? "Ocultar" : "Publicar"}
            icon={product.status === "published" ? <Archive /> : <Send />}
            onClick={onStatus}
          />
        )}
        <button
          type="button"
          onClick={onFeatured}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/20 text-black transition hover:border-black hover:bg-black hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
          aria-label={product.featured ? "Quitar de destacados" : "Destacar producto"}
          title={product.featured ? "Quitar de destacados" : "Destacar producto"}
          aria-pressed={Boolean(product.featured)}
        >
          <Star className={`h-4 w-4 ${product.featured ? "fill-current" : ""}`} />
        </button>
        <IconButton
          label={product.status === "archived" ? "Restaurar" : "Archivar"}
          icon={product.status === "archived" ? <RotateCcw /> : <Archive />}
          onClick={onArchive}
          destructive={product.status !== "archived"}
        />
      </div>
    </article>
  );
}

function ContactRow({
  contact,
  pendingAction,
  onAction,
}: {
  contact: MailchimpContact;
  pendingAction: string | null;
  onAction: (action: "archive" | "unarchive" | "delete") => void;
}) {
  const isPending = pendingAction?.endsWith(`:${contact.id}`) ?? false;
  return (
    <article className="flex flex-col gap-4 p-5 transition hover:bg-black/[0.012] sm:flex-row sm:items-center">
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
          contact.archived ? "bg-black/[0.05] text-black/40" : "bg-black text-white"
        }`}
      >
        {contact.archived ? <Archive className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate text-sm font-semibold">{contact.emailAddress}</h3>
          <span
            className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
              contact.archived
                ? "bg-black/[0.06] text-black/50"
                : contact.status === "subscribed"
                  ? "bg-black text-white"
                  : "border border-black/15 bg-white text-black/60"
            }`}
          >
            {contact.archived ? "Archivado" : statusLabel(contact.status)}
          </span>
        </div>
        <p className="mt-1 text-xs text-black/40">
          {contact.name || "Sin nombre"} ·{" "}
          {contact.archived ? formatDate(contact.archivedAt) : formatDate(contact.lastChanged)}
        </p>
        {contact.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {contact.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-black/10 bg-white px-2 py-0.5 text-[10px] text-black"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 sm:justify-end">
        {isPending ? (
          <span className="flex h-9 items-center px-3 text-xs text-black/40">
            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            Actualizando…
          </span>
        ) : contact.archived ? (
          <IconButton
            label="Desarchivar"
            icon={<RotateCcw />}
            onClick={() => onAction("unarchive")}
          />
        ) : (
          <IconButton label="Archivar" icon={<Archive />} onClick={() => onAction("archive")} />
        )}
        <IconButton
          label="Eliminar"
          icon={<Trash2 />}
          onClick={() => onAction("delete")}
          destructive
          disabled={isPending}
        />
      </div>
    </article>
  );
}

function IconButton({
  label,
  icon,
  onClick,
  destructive = false,
  disabled = false,
}: {
  label: string;
  icon: React.ReactElement<{ className?: string }>;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
        destructive
          ? "border-black text-black hover:bg-black hover:text-white"
          : "border-black/10 text-black/55 hover:border-black hover:bg-black hover:text-white"
      }`}
    >
      {cloneElement(icon, { className: "h-3.5 w-3.5" })}
      {label}
    </button>
  );
}

function LoadingBlock({ label }: { label: string }) {
  return (
    <div className="flex min-h-64 items-center justify-center text-sm text-black/40">
      <LoaderCircle className="mr-2 h-5 w-5 animate-spin text-black" />
      {label}
    </div>
  );
}

function EmptyBlock({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex min-h-64 flex-col items-center justify-center px-6 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-white">
        {icon}
      </span>
      <h3 className="mt-4 text-xl font-semibold">{title}</h3>
      <p className="mt-2 max-w-md text-sm text-black/45">{description}</p>
    </div>
  );
}
